import { useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';

/** Deliberately NOT cleared on logout - this is a per-device display choice. */
export const THEME_STORAGE_KEY = 'jm-theme';

const isTheme = (v: unknown): v is Theme => v === 'light' || v === 'dark';

const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const readStored = (): Theme | null => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : null;
  } catch {
    // Private browsing and blocked site-data both throw on access.
    return null;
  }
};

/**
 * Stored choice wins; otherwise light. Deliberately does NOT follow the OS -
 * light is the app's default and dark is opt-in via the toggle. Must stay in
 * step with the pre-paint script in index.html, or the two disagree and the
 * page flashes on load.
 */
export const resolveTheme = (): Theme => readStored() ?? 'light';

/**
 * Mobile browsers tint their own chrome (address bar, status bar) from the
 * theme-color meta. Read the ground back from the tokens rather than hardcoding
 * it here, so it cannot drift from the palette. Must run after the attribute is
 * set, so the new value has resolved.
 */
const syncThemeColorMeta = () => {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  const ground = getComputedStyle(document.documentElement)
    .getPropertyValue('--jm-ground')
    .trim();
  if (ground) meta.setAttribute('content', ground);
};

const applyToDocument = (theme: Theme) => {
  // Bootstrap 5.3 reads this attribute, and so do our own tokens in theme.css,
  // so a single switch drives both.
  document.documentElement.setAttribute('data-bs-theme', theme);

  syncThemeColorMeta();
};

// Mirrors the inline script in index.html, which sets the attribute before
// first paint. Reading back from the DOM keeps the two in sync rather than
// resolving twice and risking a mismatch.
let current: Theme = (() => {
  if (typeof document === 'undefined') return 'light';
  const onDocument = document.documentElement.getAttribute('data-bs-theme');
  if (isTheme(onDocument)) {
    // The pre-paint script set the attribute but cannot read the tokens, so the
    // meta still holds index.html's light default. Bring it in line.
    syncThemeColorMeta();
    return onDocument;
  }
  const resolved = resolveTheme();
  applyToDocument(resolved);
  return resolved;
})();

const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

/** Must stay comfortably above --jm-theme-fade in theme.css: pulling the class
 *  while a transition is still running cancels it and leaves the property stuck
 *  at its half-way value until something else forces a restyle. */
const THEME_FADE_MS = 450;
const THEMING_CLASS = 'jm-theming';
let fadeTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Applies a blanket colour transition for the length of the switch only, so the
 * palette cross-fades instead of snapping. Left on permanently it would slow
 * every hover and focus state in the app.
 */
const runThemeFade = () => {
  const root = document.documentElement;
  root.classList.add(THEMING_CLASS);
  // Force a style flush so the browser computes one frame with the transition
  // active and the OLD colours still in place. Without this the class and the
  // palette change land in a single recalculation, the transition never starts,
  // and the value snaps.
  void root.offsetWidth;
  if (fadeTimer !== undefined) clearTimeout(fadeTimer);
  fadeTimer = setTimeout(() => root.classList.remove(THEMING_CLASS), THEME_FADE_MS);
};

export const setTheme = (theme: Theme) => {
  if (theme === current) return;
  current = theme;
  // Class first: if the attribute flips in the same recalc without it, the new
  // palette paints untransitioned.
  if (!prefersReducedMotion()) runThemeFade();
  applyToDocument(theme);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Preference is lost on reload, but the current session still works.
  }
  listeners.forEach((listener) => listener());
};

export const toggleTheme = () => setTheme(current === 'dark' ? 'light' : 'dark');

export const getTheme = (): Theme => current;

/** Subscribes a component to theme changes from anywhere in the app. */
export const useTheme = (): Theme => useSyncExternalStore(subscribe, getTheme, () => 'light');
